<template>
  <canvas
    id="draw-canvas"
    ref="canvas"
    :class="{
      'cursor-crosshair': !image,
      'op-disabled': !!image,
    }"
    data-cy="esign-canvas"
    data-testId="esign-canvas"
    height="125"
    width="350"
  >
    {{ $bct('Draw your signature with your finger or mouse') }}
  </canvas>
</template>

<script setup>
const $bct = inject('translator');

defineExpose({ clear, filled, getDataUrl, stopDrawing });

const props = defineProps({
  image: { type: String, default: null },
  initialCtx: { type: Object, default: null },
  locked: { type: Boolean, default: true },
});

const canvas = ref(null);
const ctx = ref(null);
const drawing = ref(false);
const lastPos = ref({ x: 0, y: 0 });

onMounted(() => initializeEsign());

function clear() {
  // Clear the canvas by resetting its width (forces browser to clear content)
  if (!canvas.value) return;
  const currentWidth = canvas.value.width;
  canvas.value.width = currentWidth;
  setContext();
}

function filled() {
  if (!ctx.value || !canvas.value) return false;
  const pixelBuffer = new Uint32Array(
    ctx.value.getImageData(0, 0, canvas.value.width, canvas.value.height).data.buffer,
  );
  return pixelBuffer.some((color) => color !== 0);
}

function getPosFromPointerEvent(canvasDom, event) {
  const rect = canvasDom.getBoundingClientRect();
  return {
    x: event.clientX - rect.left,
    y: event.clientY - rect.top,
  };
}

function drawSegment(from, to) {
  ctx.value.beginPath();
  ctx.value.moveTo(from.x, from.y);
  ctx.value.lineTo(to.x, to.y);
  ctx.value.stroke();
}

function getDataUrl() {
  return canvas.value.toDataURL();
}

function initializeEsign() {
  if (!canvas.value) return;
  setContext();

  if (props.image) {
    loadImage();
    return;
  }

  const onPointerDown = (event) => {
    if (props.locked) return;

    // Only draw with primary pointer (avoids multi-touch weirdness)
    if (!event.isPrimary) return;

    drawing.value = true;
    lastPos.value = getPosFromPointerEvent(canvas.value, event);

    canvas.value.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!drawing.value || props.locked) return;
    if (!event.isPrimary) return;

    const pos = getPosFromPointerEvent(canvas.value, event);
    drawSegment(lastPos.value, pos);
    lastPos.value = pos;
  };

  const endStroke = (event) => {
    drawing.value = false;
    if (event?.pointerId != null && canvas.value.hasPointerCapture(event.pointerId)) {
      canvas.value.releasePointerCapture(event.pointerId);
    }
  };

  canvas.value.addEventListener('pointerdown', onPointerDown);
  canvas.value.addEventListener('pointermove', onPointerMove);
  canvas.value.addEventListener('pointerup', endStroke);
  canvas.value.addEventListener('pointercancel', endStroke);
}

function loadImage() {
  const img = new Image();
  img.onload = () => {
    ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height);
    ctx.value.drawImage(img, 0, 0, canvas.value.width, canvas.value.height);
  };
  img.src = props.image;
}

function setContext() {
  if (!canvas.value) return;
  ctx.value = props.initialCtx || canvas.value.getContext('2d');
  if (!ctx.value) return;
  ctx.value.strokeStyle = '#222222';
  ctx.value.lineWidth = 4;
  ctx.value.lineCap = 'round';
  ctx.value.lineJoin = 'round';
}

function stopDrawing() {
  drawing.value = false;
}
</script>

<style scoped>
#draw-canvas {
  border: 2px solid #cccccc;
  border-radius: 15px;
  touch-action: none;
}
</style>
